/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */


import { CustomReportSubsidiaryStats, CustomReportType, DateCustomReportData, DateReportType, SURVEY_TYPE_LABELS, TableNodeWithSurveyData } from '../../../types/custom-report';
import { Data, TableNode } from '@table-library/react-table-library/table';
import { Nullish } from '@table-library/react-table-library/common';
import { SurveyInitiative } from '../../../types/survey';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { DataPeriods, getPeriodName } from '@utils/dataPeriods';
import { DATE, formatDateUTC } from '../../../utils/date';
import { Filters } from './filters';
import { sortEffectiveDateAsc } from '@utils/sort';
import { SURVEY } from '@constants/terminology';
import { SurveySummary } from '../../../types/scorecard';

interface TreeNode {
  _id: string,
  parentId?: string
  leaf?: boolean;
  children?: TreeNode[],
  parent?: TreeNode
}

export type StringIdTableNode = Omit<TableNode, 'id' | 'nodes'> & {
  id: string;
  nodes?: StringIdTableNode[]
};

export type TableData = Data<StringIdTableNode>;

const createTreeGraph = (initiativeMap: Map<string, TreeNode>, parentId: string) => {

  const parentNode = initiativeMap.get(parentId);
  if (!parentNode) {
    return [];
  }

  const values = Array.from(initiativeMap.values());

  // Generate tree and create references
  let children = [parentNode]
  let nextChildren: TreeNode[] = [];

  while (children.length > 0) {
    nextChildren = []
    for (const c of children) {
      const innerChildren = values.filter(v => v.parentId === c._id)
      for (const inner of innerChildren) {
        inner.parent = c;
        nextChildren.push(inner);
      }
    }
    children = nextChildren;
  }

  return values;
}

export const getExpandedIds = (
  list: CustomReportSubsidiaryStats[],
  initiativeId: string,
  defaultSelected?: string[],
): string[] => {

  if (!defaultSelected) {
    return [];
  }

  const initiativeMap = new Map<string, TreeNode>(list.map(i => {
    return [i._id, { _id: i._id, parentId: i.parentId }]
  }))

  const selectedIds = new Set(defaultSelected);
  const treeMap = createTreeGraph(initiativeMap, initiativeId);

  const leafNodes = treeMap.filter(node => selectedIds.has(node._id))

  const expendedIds = new Set<string>();

  // Traverse back from leaf nodes to the top
  leafNodes.forEach(n => {
    let parent = n.parent;
    while (parent) {

      if (expendedIds.has(parent._id)) {
        break; // Already visited this part of node
      }

      expendedIds.add(parent._id);
      parent = parent.parent;
    }
  })


  return Array.from(expendedIds);
}

const findSubsidiaries = (parentId: string, list: CustomReportSubsidiaryStats[], depth: number): Pick<TableData, 'nodes'> | null => {
  const subsidiaries = list.filter(s => s.parentId === parentId);
  if (!subsidiaries || subsidiaries.length === 0) {
    return null;
  }

  return {
    nodes: subsidiaries.map(subsidiary => (
      {
        id: subsidiary._id,
        levelNum: depth,
        level: `Level ${depth}`,
        ...subsidiary,
        ...findSubsidiaries(subsidiary._id, list, depth + 1)
      }
    ))
  }
}

export const buildTree = (list: CustomReportSubsidiaryStats[], rootId: string): TableData => {
  const rootInitiative = list.find(s => s._id === rootId);
  if (!rootInitiative) {
    return { nodes: [] };
  }

  return {
    nodes: [
      {
        id: rootInitiative._id,
        levelNum: 0,
        level: 'Level 0 (Organization)',
        ...rootInitiative,
        ...findSubsidiaries(rootInitiative._id, list, 1)
      }
    ]
  };
}

const getFlatNodes = (nodes: StringIdTableNode[], filterLevel: number[], flatNodes: StringIdTableNode[]) => {
  nodes.forEach((node) => {
    if (filterLevel.includes(node.levelNum)) {
      flatNodes.push({
        ...node,
        nodes: undefined,
      });
    }

    if (node.nodes) {
      getFlatNodes(node.nodes, filterLevel, flatNodes);
    }
  });
};

export const buildList = (list: CustomReportSubsidiaryStats[], rootId: string, filterLevel: number[]): TableData => {
  const tree = buildTree(list, rootId);

  const flatNodes: StringIdTableNode[] = [];
  getFlatNodes(tree.nodes, filterLevel, flatNodes);
  return {
    nodes: flatNodes,
  };
};

export const filterData = (nodes: StringIdTableNode[] | Nullish, search: string): StringIdTableNode[] => {
  const isSearch = search && search.length >= 0;
  if (!nodes || !isSearch) {
    return nodes ?? [];
  }

  return nodes.map((node) => {
    if (node.name.toLowerCase().includes(search.toLowerCase())) {
      return node;
    }
    const filteredNodes = filterData(node.nodes, search);
    if (filteredNodes.length > 0) {
      return {
        ...node,
        nodes: filteredNodes
      };
    }
    return undefined;
  }).filter(node => node !== undefined);
}

export const findMaxDepth = (list: CustomReportSubsidiaryStats[], initiativeId: string): number => {
  const initiativeMap = new Map<string, TreeNode>(
    list.map((i) => {
      return [i._id, { _id: i._id, parentId: i.parentId }];
    })
  );

  let maxNodeDepth = 0;
  const parentNode = initiativeMap.get(initiativeId);
  if (!parentNode) {
    return maxNodeDepth;
  }

  const values = Array.from(initiativeMap.values());

  let children = [parentNode];
  let nextChildren: TreeNode[] = [];

  while (children.length > 0) {
    nextChildren = [];
    for (const child of children) {
      const innerChildren = values.filter((v) => v.parentId === child._id);
      nextChildren.push(...innerChildren);
    }
    children = nextChildren;
    maxNodeDepth++;
  }
  return maxNodeDepth;
};

export const getSelectedSurveyNodeIds = (surveys?: Pick<SurveyInitiative, 'type' | 'effectiveDate' | 'period'>[]) => {
  if (!surveys) {
    return [];
  }

  return surveys.reduce((nodeIds, survey) => {
    const { type = SurveyType.Default, period = DataPeriods.Yearly, effectiveDate } = survey;
    if (!effectiveDate) {
      return nodeIds;
    }

    const monthYear = formatDateUTC(effectiveDate, DATE.MONTH_YEAR);
    const year = formatDateUTC(effectiveDate, DATE.YEAR_ONLY);
    nodeIds.push(`${year}-${type}-${monthYear}-${period}`);

    return nodeIds;
  }, [] as string[]);
};

const CUSTOM_REPORT_TYPE_MAPPING = {
  [CustomReportType.Survey]: SURVEY.CAPITALIZED_PLURAL,
  [CustomReportType.SurveyAggregation]: `Combined ${SURVEY.PLURAL}`,
};

export const getSurveyName = (survey: SurveySummary) =>
  `${formatDateUTC(survey.effectiveDate, DATE.MONTH_ONLY)}${survey.name ? ` - ${survey.name}` : ''}`;

export const buildDateSurveyTree = (surveys: SurveySummary[], filters: Filters<DateReportType>): { data: TableData; numOfNodes: number } => {
  const surveyTypeMap = new Map<string, Map<DataPeriods, Map<string, Map<string, DateCustomReportData>>>>();

  surveys.forEach((survey) => {
    const { _id, type = SurveyType.Default, period = DataPeriods.Yearly, status, effectiveDate } = survey;
    const surveyName = getSurveyName(survey);
    const surveyItem = { _id, effectiveDate, type, period, status, surveyName };
    const year = formatDateUTC(survey.effectiveDate, DATE.YEAR_ONLY);
    const surveyType = SURVEY_TYPE_LABELS[type];

    if (!surveyTypeMap.has(surveyType)) {
      surveyTypeMap.set(surveyType, new Map());
    }

    const periodMap = surveyTypeMap.get(surveyType);

    if (!periodMap) {
      return;
    }

    if (!periodMap.has(period)) {
      periodMap.set(period, new Map());
    }
    const yearMap = periodMap.get(period);
    if (!yearMap) {
      return;
    }

    if (!yearMap.has(year)) {
      yearMap.set(year, new Map());
    }

    const surveyMap = yearMap.get(year);

    if (!surveyMap) {
      return;
    }
    if (!surveyMap.has(surveyName)) {
      surveyMap.set(surveyName, surveyItem);
    }
  });

  const data: TableData = {
    nodes: [],
  };
  let numOfNodes = 0;

  surveyTypeMap.forEach((periodMap, surveyType) => {
    if (filters.surveyType.length && !filters.surveyType.map(type => CUSTOM_REPORT_TYPE_MAPPING[type]).includes(surveyType)) {
      return;
    }

    const surveyTypeNodes: StringIdTableNode[] = [];
    periodMap.forEach((yearMap, period) => {
      if (filters.period.length && !filters.period.includes(period)) {
        return;
      }

      const periodNodes: StringIdTableNode[] = [];
      yearMap.forEach((surveyMap, year) => {
        if (filters.year.length && !filters.year.includes(year)) {
          return;
        }
        const surveyNodes: TableNodeWithSurveyData[] = [];
        surveyMap.forEach((surveyItem, surveyName) => {
          const date = surveyItem.effectiveDate;

          if (filters.month.length && !filters.month.includes(formatDateUTC(date, 'M'))) {
            return;
          }

          surveyNodes.push({
            id: `${surveyType}-${period}-${year}-${surveyName}`,
            name: surveyItem.surveyName,
            ...surveyItem,
          });
        });

        numOfNodes += surveyNodes.length;
        periodNodes.push({
          id: `${surveyType}-${period}-${year}`,
          name: year,
          nodes: surveyNodes.sort(sortEffectiveDateAsc),
        });
      });
      surveyTypeNodes.push({
        id: `${surveyType}-${period}`,
        name: getPeriodName(period),
        nodes: periodNodes.sort((a, b) => b.name - a.name),
      });
    });

    data.nodes.push({
      id: surveyType,
      name: surveyType,
      nodes: surveyTypeNodes,
    });
  });

  return { data, numOfNodes };
};
